import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  AlertCircle,
  Banknote,
  Layers,
  Loader2,
  Package,
  Plus,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./PurchaseOrder.css";

const url = process.env.REACT_APP_BACKEND_URL;

const emptyItem = {
  product_id: "",
  quantity: "",
  unit_price: "",
  total_price: "0",
  notes: "",
};

const PurchaseOrder = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productStock, setProductStock] = useState({});
  const [productPrices, setProductPrices] = useState({});

  // Order-level discount and tax choices.
  const [discounts, setDiscounts] = useState([]);
  const [taxes, setTaxes] = useState([]);

  const [formData, setFormData] = useState({
    warehouse_id: "",
    supplier_id: "",
    transaction_type_id: "",
    order_no: "",
    order_date: new Date().toISOString().split("T")[0],
    expected_delivery_date: "",
    status: "draft",
    supplier_notes: "",
    shipping_cost: "0",
    other_charges: "0",
    requires_approval: false,

    // Apply one selected discount and tax to the whole order.
    discount_id: "",
    tax_id: "",

    // Additional manual adjustments.
    manual_discount: "",
    // manual_tax removed
  });

  const [items, setItems] = useState([{ ...emptyItem }]);

  const isSystemAdmin =
    userType === "system" &&
    (currentUser?.role === "developer" || currentUser?.role === "admin");

  const isCompanyAdmin =
    userType === "company" &&
    ["admin", "purchase", "user"].includes(currentUser?.role);

  const canCreate = isCompanyAdmin || isSystemAdmin;

  const getCompanyId = () => {
    if (userType === "company" && company) {
      return company._id || company;
    }
    return null;
  };

  const fetchPricingOptions = async () => {
    try {
      const companyId = getCompanyId();
      if (!companyId) return;

      /*
       * This endpoint may still return payment methods, but this page ignores them.
       * A Purchase Order is always Credit (Pay Later), with Accounts Payable automatic.
       */
      const response = await axios.get(
        `${url}/api/purchaseorders/payment-methods`,
        {
          params: { company_id: companyId },
        }
      );

      const options = response.data?.data || {};
      setDiscounts(options.discounts || []);
      setTaxes(options.taxes || []);
    } catch (requestError) {
      setDiscounts([]);
      setTaxes([]);
    }
  };

  const fetchProductPurchasePrice = async (productId) => {
    if (!productId) return 0;

    if (productPrices[productId] !== undefined) {
      return productPrices[productId];
    }

    try {
      const companyId = getCompanyId();

      const response = await axios.get(`${url}/api/products/${productId}`, {
        params: { company_id: companyId },
      });

      if (response.data.success) {
        const price =
          response.data.data.purchase_price ||
          response.data.data.unit_price ||
          0;

        setProductPrices((previous) => ({
          ...previous,
          [productId]: price,
        }));

        return price;
      }

      return 0;
    } catch {
      return 0;
    }
  };

  const fetchProductsForWarehouse = async (warehouseId) => {
    if (!warehouseId) {
      setFilteredProducts(products);
      setProductStock({});
      return;
    }

    try {
      const companyId = getCompanyId();

      const response = await axios.get(
        `${url}/api/inventorystock/warehouse/${warehouseId}`,
        {
          params: { company_id: companyId, status: "active" },
        }
      );

      if (!response.data.success) {
        setFilteredProducts(products);
        setProductStock({});
        return;
      }

      const stockMap = {};
      const warehouseProducts = [];

      for (const stock of response.data.data || []) {
        let productId;
        let productName;
        let productCode;
        let purchasePrice = 0;

        if (stock.product_id && typeof stock.product_id === "object") {
          productId = stock.product_id._id;
          productName = stock.product_id.product_name;
          productCode = stock.product_id.product_code;
          purchasePrice =
            stock.product_id.purchase_price ||
            stock.product_id.unit_price ||
            0;
        } else {
          productId = stock.product_id;
          const foundProduct = products.find(
            (product) => product._id === productId
          );

          productName = foundProduct?.product_name || "Unknown";
          productCode = foundProduct?.product_code || "N/A";
          purchasePrice =
            foundProduct?.purchase_price || foundProduct?.unit_price || 0;
        }

        stockMap[productId] = {
          current_stock: stock.quantity || 0,
          available_stock: stock.available_qty || 0,
          reserved_stock: stock.reserved_qty || 0,
          avg_cost: stock.avg_cost || 0,
        };

        warehouseProducts.push({
          _id: productId,
          product_code: productCode,
          product_name: productName,
          available_stock: stock.available_qty || 0,
          purchase_price: purchasePrice,
        });

        if (purchasePrice > 0) {
          setProductPrices((previous) => ({
            ...previous,
            [productId]: purchasePrice,
          }));
        }
      }

      setProductStock(stockMap);
      setFilteredProducts(warehouseProducts);
    } catch (requestError) {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!canCreate) {
        toast.error("You don't have permission");
        navigate("/");
        return;
      }

      try {
        setLoading(true);

        const companyId = getCompanyId();
        if (!companyId) {
          setError("No company ID found");
          return;
        }

        const params = { company_id: companyId, status: "active" };

        const [warehouseResponse, supplierResponse, typeResponse, productResponse] =
          await Promise.all([
            axios.get(`${url}/api/warehouse/dropdown`, { params }),
            axios.get(`${url}/api/supplier/dropdown`, { params }),
            axios.get(`${url}/api/transactiontype/dropdown`, { params }),
            axios.get(`${url}/api/products/dropdown`, { params }),
          ]);

        if (warehouseResponse.data?.success) {
          setWarehouses(warehouseResponse.data.data || []);
        }

        if (supplierResponse.data?.success) {
          setSuppliers(supplierResponse.data.data || []);
        }

        if (typeResponse.data?.success) {
          const types = typeResponse.data.data || [];
          const purchaseOrderTypes = types.filter(
            (type) =>
              (type.transaction_type || "").toLowerCase() === "purchase_order"
          );

          setTransactionTypes(purchaseOrderTypes);

          const defaultType = purchaseOrderTypes[0];
          if (defaultType) {
            setFormData((previous) => ({
              ...previous,
              transaction_type_id: defaultType._id,
            }));
          }
        }

        if (productResponse.data?.success) {
          const productsData = productResponse.data.data || [];
          const priceMap = {};

          for (const product of productsData) {
            const price =
              product.purchase_price || product.unit_price || 0;

            if (price > 0) {
              priceMap[product._id] = price;
            }
          }

          setProducts(productsData);
          setFilteredProducts(productsData);
          setProductPrices(priceMap);
        }

        await fetchPricingOptions();
      } catch (requestError) {
        setError(requestError.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWarehouseChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    // Clear selected products when changing warehouse, preventing stale stock.
    setItems([{ ...emptyItem }]);

    if (value) {
      fetchProductsForWarehouse(value);
    } else {
      setFilteredProducts(products);
      setProductStock({});
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    if (
      ["shipping_cost", "other_charges", "manual_discount"].includes(
        name
      )
    ) {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) return;
    }

    setFormData((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    setItems((previous) =>
      previous.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const updatedItem = {
          ...item,
          [field]: value,
        };

        const quantity = Number(updatedItem.quantity) || 0;
        const unitPrice = Number(updatedItem.unit_price) || 0;
        updatedItem.total_price = (quantity * unitPrice).toFixed(2);

        return updatedItem;
      })
    );
  };

  const handleProductSelect = async (index, productId) => {
    if (!productId) {
      handleItemChange(index, "product_id", "");
      return;
    }

    const duplicate = items.some(
      (item, itemIndex) =>
        itemIndex !== index &&
        item.product_id === productId
    );

    if (duplicate) {
      toast.error("This product is already selected");
      return;
    }

    try {
      let purchasePrice = productPrices[productId];

      if (purchasePrice === undefined) {
        purchasePrice = await fetchProductPurchasePrice(productId);
      }

      setItems((previous) =>
        previous.map((item, itemIndex) => {
          if (itemIndex !== index) return item;

          const quantity = Number(item.quantity) || 0;
          const unitPrice = Number(purchasePrice) || 0;

          return {
            ...item,
            product_id: productId,
            unit_price: String(unitPrice),
            total_price: (quantity * unitPrice).toFixed(2),
          };
        })
      );
    } catch {
      toast.error("Failed to fetch product price");
    }
  };
  const getAvailableProductsForRow = (index) => {
    const selectedProductIds = items
      .map((item, itemIndex) =>
        itemIndex !== index ? item.product_id : null
      )
      .filter(Boolean);

    return filteredProducts.filter(
      (product) => !selectedProductIds.includes(product._id)
    );
  };
  const addItemRow = () => {
    setItems((previous) => [...previous, { ...emptyItem }]);
  };

  const removeItemRow = (index) => {
    setItems((previous) => {
      if (previous.length <= 1) return previous;
      return previous.filter((_, itemIndex) => itemIndex !== index);
    });
  };

  const totals = useMemo(() => {
    const subTotal = items.reduce((sum, item) => {
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unit_price) || 0;
      return sum + quantity * unitPrice;
    }, 0);

    const selectedDiscount = discounts.find(
      (discount) => discount._id === formData.discount_id
    );

    let selectedDiscountAmount = 0;

    if (selectedDiscount?.discount_type === "percentage") {
      selectedDiscountAmount =
        (subTotal * Number(selectedDiscount.value || 0)) / 100;
    } else if (selectedDiscount?.discount_type === "fixed") {
      selectedDiscountAmount = Number(selectedDiscount.value || 0);
    }

    selectedDiscountAmount = Math.min(
      Math.max(0, selectedDiscountAmount),
      subTotal
    );

    const manualDiscountRequested = Math.max(
      0,
      Number(formData.manual_discount) || 0
    );

    const manualDiscountAmount = Math.min(
      manualDiscountRequested,
      Math.max(0, subTotal - selectedDiscountAmount)
    );

    const totalDiscount =
      selectedDiscountAmount + manualDiscountAmount;

    const taxableAmount = Math.max(0, subTotal - totalDiscount);

    const selectedTax = taxes.find((tax) => tax._id === formData.tax_id);

    let selectedTaxAmount = 0;
    let selectedTaxAddedToTotal = 0;

    if (selectedTax?.tax_type === "percentage") {
      const rate = Number(selectedTax.value || 0);

      if (selectedTax.price_inclusive) {
        selectedTaxAmount =
          rate > 0
            ? taxableAmount - taxableAmount / (1 + rate / 100)
            : 0;
      } else {
        selectedTaxAmount = (taxableAmount * rate) / 100;
        selectedTaxAddedToTotal = selectedTaxAmount;
      }
    } else if (selectedTax?.tax_type === "fixed") {
      selectedTaxAmount = Math.max(0, Number(selectedTax.value || 0));

      if (!selectedTax.price_inclusive) {
        selectedTaxAddedToTotal = selectedTaxAmount;
      }
    }

    // manual_tax removed – only selected tax from tax_id is used
    const totalTax = selectedTaxAmount;

    const shippingCost = Math.max(
      0,
      Number(formData.shipping_cost) || 0
    );

    const otherCharges = Math.max(
      0,
      Number(formData.other_charges) || 0
    );

    const grandTotal =
      taxableAmount +
      selectedTaxAddedToTotal +
      shippingCost +
      otherCharges;

    return {
      subTotal,
      selectedDiscountAmount,
      manualDiscountAmount,
      totalDiscount,
      taxableAmount,
      selectedTaxAmount,
      // manualTaxAmount removed
      totalTax,
      shippingCost,
      otherCharges,
      grandTotal,
    };
  }, [items, discounts, taxes, formData]);

  const validateForm = () => {
    if (!formData.warehouse_id) {
      toast.error("Warehouse is required");
      return false;
    }

    if (!formData.supplier_id) {
      toast.error("Supplier is required");
      return false;
    }

    const validItems = items.filter(
      (item) =>
        item.product_id &&
        Number(item.quantity) > 0 &&
        Number(item.unit_price) >= 0
    );

    if (validItems.length === 0) {
      toast.error("At least one valid item is required");
      return false;
    }

    if (
      Number(formData.manual_discount || 0) >
      totals.subTotal - totals.selectedDiscountAmount
    ) {
      toast.error("Manual discount cannot exceed the remaining subtotal");
      return false;
    }

    if (
      formData.status === "confirmed" ||
      formData.status === "processing"
    ) {
      for (const item of validItems) {
        const requestedQuantity = Number(item.quantity);
        const stockInfo = productStock[item.product_id];

        if (
          stockInfo &&
          Number(stockInfo.available_stock) < requestedQuantity
        ) {
          toast.error(
            `Insufficient available stock. Available: ${stockInfo.available_stock}, Requested: ${requestedQuantity}`
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) return;

    setSubmitting(true);

    try {
      const companyId = getCompanyId();

      const validItems = items.filter(
        (item) =>
          item.product_id &&
          Number(item.quantity) > 0 &&
          Number(item.unit_price) >= 0
      );

      const payload = {
        order: {
          company_id: companyId,
          supplier_id: formData.supplier_id,
          warehouse_id: formData.warehouse_id,
          transaction_type_id: formData.transaction_type_id,
          order_no: formData.order_no || "",
          order_date: formData.order_date,
          expected_delivery_date:
            formData.expected_delivery_date || null,
          status: formData.status,
          supplier_notes: formData.supplier_notes,
          shipping_cost: Number(formData.shipping_cost) || 0,
          other_charges: Number(formData.other_charges) || 0,
          requires_approval: formData.requires_approval,

          // Whole-order discount/tax.
          discount_id: formData.discount_id || null,
          tax_id: formData.tax_id || null,
          manual_discount: Number(formData.manual_discount) || 0,
          // manual_tax removed

          // Calculated values sent for review; backend must recalculate/validate.
          subtotal: totals.subTotal,
          total_discount: totals.totalDiscount,
          total_tax: totals.totalTax,
          grand_total: totals.grandTotal,

          /*
           * Important: Purchase Order creation must not post journal,
           * ledger, payment, cash, bank, or supplier financial entries.
           * The backend create handler must honor this flag.
           */
          skip_financial_posting: true,
          post_to_ledger: false,
        },

        items: validItems.map((item) => ({
          product_id: item.product_id,
          quantity: Number(item.quantity),
          unit_price: Number(item.unit_price),
          notes: item.notes || "",
        })),

        /*
         * Only the credit/pay-later descriptor is passed.
         * No account is selected and no payment amount is posted.
         */
        payments: [
          {
            method: "credit",
            account_id: null,
            amount: 0,
            reference: "",
            notes: "Accounts Payable (automatic)",
          },
        ],
      };

      const response = await axios.post(
        `${url}/api/purchaseorders`,
        payload
      );

      if (response.data.success) {
        toast.success(
          `Purchase order created! Reference: ${response.data.data.reference_id}`
        );

        setTimeout(() => navigate("/ViewPurchaseOrder"), 1500);
      }
    } catch (requestError) {
      toast.error(
        requestError.response?.data?.message ||
        "Failed to create purchase order"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="order-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading purchase order form...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-wrapper">
        <div className="order-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h3>Error Loading Data</h3>
            <p>{error}</p>
            <button
              onClick={() => navigate("/ViewPurchaseOrder")}
              className="cancel-btn"
              type="button"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="order-form-container">
        <button
          className="close-btn"
          onClick={() => navigate("/ViewPurchaseOrder")}
          type="button"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <Package size={24} />
          <h2>Create Purchase Order</h2>
        </div>

        <div
          style={{
            marginBottom: 20,
            display: "flex",
            gap: 10,
            alignItems: "center",
            padding: 10,
            background:
              formData.status === "draft"
                ? "#fff7e6"
                : formData.status === "confirmed"
                  ? "#e6f7ff"
                  : "#f9f0ff",
            border: `1px solid ${formData.status === "draft"
              ? "#ffd591"
              : formData.status === "confirmed"
                ? "#91d5ff"
                : "#d3adf7"
              }`,
            borderRadius: 4,
          }}
        >
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            style={{
              padding: "8px 16px",
              background: "white",
              border: "1px solid #ddd",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            <option value="draft">
              📝 Draft - No stock reservation
            </option>
            <option value="confirmed">
              ✅ Confirmed - Stock will be reserved
            </option>
            <option value="processing">
              ⚙️ Processing - Stock reserved
            </option>
          </select>

          <span
            style={{
              color:
                formData.status === "draft"
                  ? "#fa8c16"
                  : formData.status === "confirmed"
                    ? "#1890ff"
                    : "#722ed1",
              fontWeight: 500,
              fontSize: 13,
            }}
          >
            {formData.status === "draft" && "• No stock reservation"}
            {formData.status === "confirmed" &&
              "• Stock will be reserved when saved"}
            {formData.status === "processing" &&
              "• Stock is reserved"}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Warehouse *
              </label>
              <Select
                value={
                  warehouses
                    .filter(
                      (warehouse) =>
                        String(warehouse._id) === String(formData.warehouse_id)
                    )
                    .map((warehouse) => ({
                      value: warehouse._id,
                      label: `${warehouse.warehouse_name} (${warehouse.warehouse_code})`,
                    }))[0] || null
                }

                options={warehouses.map((warehouse) => ({
                  value: warehouse._id,
                  label: `${warehouse.warehouse_name} (${warehouse.warehouse_code})`,
                }))}

                onChange={(selected) =>
                  handleWarehouseChange({
                    target: {
                      name: "warehouse_id",
                      value: selected ? selected.value : "",
                    },
                  })
                }

                placeholder="Search Warehouse..."

                isClearable

                noOptionsMessage={() => "No warehouse found"}

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: 4,
                    border: "1px solid #ddd",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),

                  menuList: (base) => ({
                    ...base,
                    maxHeight: 250,
                  }),
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Supplier *
              </label>
              <Select
                value={
                  suppliers
                    .filter(
                      (supplier) =>
                        String(supplier._id) === String(formData.supplier_id)
                    )
                    .map((supplier) => ({
                      value: supplier._id,
                      label: `${supplier.supplier_name} (${supplier.supplier_id}${supplier.phone ? ` - ${supplier.phone}` : ''})`,
                    }))[0] || null
                }

                options={suppliers.map((supplier) => ({
                  value: supplier._id,
                  label: `${supplier.supplier_name} (${supplier.supplier_id}${supplier.phone ? ` - ${supplier.phone}` : ''})`,
                }))}

                onChange={(selected) =>
                  handleChange({
                    target: {
                      name: "supplier_id",
                      value: selected ? selected.value : "",
                    },
                  })
                }

                placeholder="Search Supplier..."

                isClearable

                noOptionsMessage={() => "No supplier found"}

                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "38px",
                    borderRadius: 4,
                    border: "1px solid #ddd",
                  }),

                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),

                  menuList: (base) => ({
                    ...base,
                    maxHeight: 250,
                  }),
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Transaction Type
              </label>
              <select
                name="transaction_type_id"
                value={formData.transaction_type_id}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
                required
              >
                <option value="">Select Type</option>
                {transactionTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.transaction_type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Order No (Optional)
              </label>
              <input
                type="text"
                name="order_no"
                value={formData.order_no}
                onChange={handleChange}
                placeholder="Auto-generated"
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Order Date
              </label>
              <input
                type="date"
                name="order_date"
                value={formData.order_date}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Expected Delivery
              </label>
              <input
                type="date"
                name="expected_delivery_date"
                value={formData.expected_delivery_date}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>
          </div>

          <h3
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 15,
            }}
          >
            <Package size={18} />
            Order Items
            {formData.warehouse_id && (
              <span
                style={{
                  fontSize: 13,
                  color: "#666",
                  fontWeight: "normal",
                }}
              >
                (Stock available for selected warehouse)
              </span>
            )}
          </h3>

          {items.map((item, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ddd",
                padding: 15,
                marginBottom: 15,
                borderRadius: 4,
                position: "relative",
                background: "#fafafa",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "3fr 1fr 1fr 1fr",
                  gap: 10,
                }}
              >
                <div>
                  <label style={{ fontSize: 12, color: "#666" }}>
                    Product
                  </label>
                  <Select
                    value={
                      getAvailableProductsForRow(index)
                        .filter((product) => product._id === item.product_id)
                        .map((product) => ({
                          value: product._id,
                          label:
                            `${product.product_name} (${product.product_code})` +
                            (productStock[product._id]
                              ? ` | Stock: ${productStock[product._id].available_stock}`
                              : "") +
                            (productPrices[product._id] !== undefined
                              ? ` | Price: ${formatCurrency(productPrices[product._id])}`
                              : ""),
                        }))[0] || null
                    }

                    options={getAvailableProductsForRow(index).map((product) => ({
                      value: product._id,
                      label:
                        `${product.product_name} (${product.product_code})` +
                        (productStock[product._id]
                          ? ` | Stock: ${productStock[product._id].available_stock}`
                          : "") +
                        (productPrices[product._id] !== undefined
                          ? ` | Price: ${formatCurrency(productPrices[product._id])}`
                          : ""),
                    }))}

                    onChange={(selected) =>
                      handleProductSelect(
                        index,
                        selected ? selected.value : ""
                      )
                    }

                    placeholder="Search Product..."

                    isClearable

                    noOptionsMessage={() => "No product found"}

                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "38px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                      }),

                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),

                      menuList: (base) => ({
                        ...base,
                        maxHeight: 250,
                      }),
                    }}
                  />

                  {item.product_id &&
                    productStock[item.product_id] && (
                      <div
                        style={{
                          fontSize: 11,
                          marginTop: 4,
                          color: "#52c41a",
                        }}
                      >
                        <Layers
                          size={12}
                          style={{
                            display: "inline",
                            marginRight: 4,
                          }}
                        />
                        Available:{" "}
                        {
                          productStock[item.product_id]
                            .available_stock
                        }{" "}
                        units
                      </div>
                    )}
                </div>

                <div>
                  <label style={{ fontSize: 12, color: "#666" }}>
                    Quantity
                  </label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(event) =>
                      handleItemChange(
                        index,
                        "quantity",
                        event.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ddd",
                    }}
                    required
                    min="1"
                    step="1"
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, color: "#666" }}>
                    Unit Price
                  </label>
                  <input
                    type="number"
                    value={item.unit_price}
                    onChange={(event) =>
                      handleItemChange(
                        index,
                        "unit_price",
                        event.target.value
                      )
                    }
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ddd",
                    }}
                    required
                    min="0"
                    step="0.01"
                  />
                </div>

                <div>
                  <label style={{ fontSize: 12, color: "#666" }}>
                    Line Total
                  </label>
                  <input
                    type="text"
                    value={formatCurrency(item.total_price)}
                    readOnly
                    style={{
                      width: "100%",
                      padding: 8,
                      borderRadius: 4,
                      border: "1px solid #ddd",
                      background: "#f5f5f5",
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: 10 }}>
                <input
                  type="text"
                  placeholder="Item notes (optional)"
                  value={item.notes}
                  onChange={(event) =>
                    handleItemChange(
                      index,
                      "notes",
                      event.target.value
                    )
                  }
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 4,
                    border: "1px solid #ddd",
                  }}
                />
              </div>

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeItemRow(index)}
                  style={{
                    position: "absolute",
                    top: 5,
                    right: 5,
                    background: "#fff1f0",
                    border: "1px solid #ffccc7",
                    borderRadius: 4,
                    color: "#ff4d4f",
                    cursor: "pointer",
                    padding: "4px 8px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <Trash2 size={14} />
                  Remove
                </button>
              )}
            </div>
          ))}

          <button
            type="button"
            onClick={addItemRow}
            style={{
              marginBottom: 20,
              padding: "8px 16px",
              background: "transparent",
              border: "2px dashed #1890ff",
              borderRadius: 4,
              color: "#1890ff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
            disabled={!formData.warehouse_id}
          >
            <Plus size={16} />
            Add Item
          </button>

          {!formData.warehouse_id && (
            <div
              style={{
                fontSize: 13,
                color: "#fa8c16",
                marginBottom: 20,
              }}
            >
              Please select a warehouse first
            </div>
          )}

          <h3 style={{ marginBottom: 15 }}>
            Order Discount and Tax
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
              padding: 15,
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
            }}
          >
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Apply Discount to All Items
              </label>
              <select
                name="discount_id"
                value={formData.discount_id}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              >
                <option value="">No Discount</option>
                {discounts.map((discount) => (
                  <option key={discount._id} value={discount._id}>
                    {discount.name} ({discount.code}) -{" "}
                    {discount.discount_type === "percentage"
                      ? `${discount.value}%`
                      : formatCurrency(discount.value)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Apply Tax to All Items
              </label>
              <select
                name="tax_id"
                value={formData.tax_id}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              >
                <option value="">No Tax</option>
                {taxes.map((tax) => (
                  <option key={tax._id} value={tax._id}>
                    {tax.name} ({tax.code}) -{" "}
                    {tax.tax_type === "percentage"
                      ? `${tax.value}%`
                      : formatCurrency(tax.value)}
                    {" · "}
                    {tax.price_inclusive
                      ? "Inclusive"
                      : "Exclusive"}
                    {tax.recoverable ? " · Recoverable" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: 5,
                  fontWeight: 500,
                }}
              >
                Manual Discount Amount
              </label>
              <input
                type="text"
                inputMode="decimal"
                name="manual_discount"
                value={formData.manual_discount}
                onChange={handleChange}
                placeholder="Example: 5.00"
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
              <small style={{ color: "#666" }}>
                This amount is added to the selected discount.
              </small>
            </div>

            {/* Manual Tax input removed */}
          </div>

          <h3
            style={{
              marginBottom: 15,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Banknote size={18} />
            Payment Details
          </h3>

          <div
            style={{
              marginBottom: 20,
              padding: 15,
              background: "#fafafa",
              borderRadius: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 2fr 1fr",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                    display: "block",
                  }}
                >
                  Payment Method
                </label>
                <input
                  type="text"
                  value="Credit (Pay Later)"
                  readOnly
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 4,
                    border: "1px solid #ddd",
                    background: "#f5f5f5",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                    display: "block",
                  }}
                >
                  Account
                </label>
                <input
                  type="text"
                  value="Accounts Payable (automatic)"
                  readOnly
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 4,
                    border: "1px solid #ddd",
                    background: "#f5f5f5",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    fontSize: 12,
                    color: "#666",
                    marginBottom: 4,
                    display: "block",
                  }}
                >
                  Amount
                </label>
                <input
                  type="text"
                  value="Remaining balance"
                  readOnly
                  style={{
                    width: "100%",
                    padding: 8,
                    borderRadius: 4,
                    border: "1px solid #ddd",
                    background: "#f5f5f5",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                marginTop: 10,
                fontSize: 12,
                color: "#666",
              }}
            >
              Purchase Order creation does not record a payment or
              create a financial posting.
            </div>
          </div>

          <h3 style={{ marginBottom: 15 }}>Additional Charges</h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 20,
              marginBottom: 20,
            }}
          >
            <div>
              <label style={{ display: "block", marginBottom: 5 }}>
                Shipping Cost
              </label>
              <input
                type="text"
                inputMode="decimal"
                name="shipping_cost"
                value={formData.shipping_cost}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", marginBottom: 5 }}>
                Other Charges
              </label>
              <input
                type="text"
                inputMode="decimal"
                name="other_charges"
                value={formData.other_charges}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: 8,
                  borderRadius: 4,
                  border: "1px solid #ddd",
                }}
              />
            </div>
          </div>

          <h3 style={{ marginBottom: 15 }}>Notes</h3>

          <div style={{ marginBottom: 20 }}>
            <textarea
              name="supplier_notes"
              placeholder="Supplier notes..."
              value={formData.supplier_notes}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: 8,
                minHeight: 80,
                borderRadius: 4,
                border: "1px solid #ddd",
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                name="requires_approval"
                checked={formData.requires_approval}
                onChange={handleChange}
              />
              This order requires approval
            </label>
          </div>

          <div
            style={{
              background: "#f5f5f5",
              padding: 20,
              borderRadius: 4,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Subtotal:</span>
              <strong>{formatCurrency(totals.subTotal)}</strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Selected Discount:</span>
              <strong style={{ color: "#e53935" }}>
                -{formatCurrency(totals.selectedDiscountAmount)}
              </strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Manual Discount:</span>
              <strong style={{ color: "#e53935" }}>
                -{formatCurrency(totals.manualDiscountAmount)}
              </strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Total Discount:</span>
              <strong style={{ color: "#e53935" }}>
                -{formatCurrency(totals.totalDiscount)}
              </strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Selected Tax:</span>
              <strong>
                +{formatCurrency(totals.selectedTaxAmount)}
              </strong>
            </div>

            {/* Manual Tax row removed */}

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Total Tax:</span>
              <strong>+{formatCurrency(totals.totalTax)}</strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Shipping:</span>
              <strong>+{formatCurrency(totals.shippingCost)}</strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <span>Other:</span>
              <strong>+{formatCurrency(totals.otherCharges)}</strong>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: "bold",
                marginTop: 10,
                paddingTop: 10,
                borderTop: "2px solid #ddd",
              }}
            >
              <span>Grand Total:</span>
              <strong
                style={{
                  color: "#1890ff",
                  fontSize: 18,
                }}
              >
                {formatCurrency(totals.grandTotal)}
              </strong>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: 10,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              onClick={() => navigate("/ViewPurchaseOrder")}
              disabled={submitting}
              style={{
                padding: "10px 20px",
                background: "white",
                border: "1px solid #ddd",
                borderRadius: 4,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <X size={16} />
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: "10px 20px",
                background: "#1890ff",
                color: "white",
                border: "none",
                borderRadius: 4,
                cursor: submitting ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Create Order
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PurchaseOrder;
